console.debug("LOADING AdsService")
import { flow /*, observable, computed, action */ } from "mobx"
import { rootStore } from "store/RootStore"
import { TraxisCpeIdMixin } from "store/api/mixin/TraxisCpeIdMixin"
// MTVW-585: no retries
import { fetchWithRetry, NO_RETRIES, DEFAULT_TIMEOUT } from "./FetchWithRetry"
import { serviceUrls } from "store/qlapi/ServiceUrls"
import { list, object, serializable, deserialize } from "serializr"
import { getQlHeaders } from "store/qlapi/QlHeaders"
import { appFeatures } from "AppFeatures"

import { getCoCoModule } from "store/page/repo/CoCoModule"
const CoCo = await getCoCoModule()

const CREDENTIALS = 'include'
const STD_HEADERS = {
	'Accept': 'application/json',
	// Would cause CORS in keepAlive
	//'pragma': 'no-cache', 'cache-control': 'no-cache'
}
export class AdsService extends TraxisCpeIdMixin {

	get ADS_TIMEOUT() {
		//console.debug("ADS_TIMEOUT is", appFeatures.persistedAdsTimeout)
		return appFeatures.persistedAdsTimeout
	}
	get xHeaders() {
		// make a copy of STD_HEADERS, otherwise STD_HEADERS would be changed!
		const headers = {}
		Object.assign(headers, STD_HEADERS)
		Object.assign(headers, getQlHeaders())
		if (rootStore?.sso?.browserStore?.adsMock) headers['X-Quickline-Ad-Mock'] = rootStore?.sso?.browserStore?.adsMock
		return headers
	}

	_logDuration(start, info) {
		const duration = Math.trunc(performance.now() - start)
		// return asap, defer analytics log slightly
		setTimeout(() => {
			console.debug(`${info} duration %s ms`, duration)
			// no requestId for CoCo calls
			rootStore.logService.analyticsAsync("cocoRequestDuration", duration, {
				info: info, client: "web", environment: serviceUrls.environment
			})
		}, 1)
	}

	_logAdStats(adType, hasAd, channelId) {
		// no requestId for CoCo calls
		rootStore.logService.analyticsAsync("gt12AdStats", null, {
			client: "web", adType: adType, hasAd: hasAd, channelId: channelId, environment: serviceUrls.environment
		})
	}

	setAdsMockstate(enable) {
		CoCo.setFeatureFlagEnabled(CoCo.FeatureFlag.Gt12Mocked, enable)
	}

	version = flow(function* () {
		let resultJson = yield CoCo.apiFetchAdsVersionRaw()
		// CoCo returns string instead of object, need to convert to object
		if (typeof resultJson === "string") resultJson = JSON.parse(resultJson)
		return `${resultJson["build_tag"]} (${resultJson["build_date"]})`
	})

	versionUrl() {
		return [serviceUrls.adsUrl + "version", this.xHeaders]
	}

	startLiveAsync = flow(function* (channelId, epgEventStart, epgEventEnd) {
		let result = null
		try {
			const now = performance.now()
			result = yield CoCo.apiStartLiveAdSession(channelId, BigInt(epgEventStart), BigInt(epgEventEnd))
			this._logDuration(now, "apiStartLiveAdSession")
			console.debug("startLiveAsyncCoCo %o", result?.sessionId, result)
		} catch (error) {
			// handled in SourceAbstract
			throw error
		}
		return result
	})

	startReplayAsync = flow(function* (channelId, epgEventStart, epgEventEnd, bookmarkPosition) {
		let result = null
		try {
			const now = performance.now()
			result = yield CoCo.apiStartReplayAdSession(channelId, BigInt(epgEventStart), BigInt(epgEventEnd), bookmarkPosition ? BigInt(bookmarkPosition) : null)
			console.debug("startReplayAsyncCoCo %o", result?.sessionId, result)
			this._logDuration(now, "apiStartReplayAdSession")
			this._logAdStats("Replay-Ad", result?.replayAd !== null, channelId)
		} catch (error) {
			// handled in SourceAbstract
			throw error
		}
		return result 
	})

	// replayWindow: streamLifetime from media MS converted to ms
	// moment.duration(streamLifetime, moment.ISO_8601).valueOf()
	startRecordingAsync = flow(function* (channelId, recordingId, epgEventStart, epgEventEnd, isSeriesRecording, replayWindow, bookmarkPosition, bookingTime) {
		let result = null
		try {
			const now = performance.now()
			result = yield CoCo.apiStartRecordingAdSession(channelId, recordingId, BigInt(epgEventStart), BigInt(epgEventEnd), bookingTime ? BigInt(bookingTime) : null, bookmarkPosition ? BigInt(bookmarkPosition) : null, isSeriesRecording, replayWindow ? BigInt(replayWindow) : null)
			console.debug("startRecordingAsyncCoCo %o", result?.sessionId, result)
			this._logDuration(now, "apiStartRecordingAdSession")
			this._logAdStats("Replay-Ad", result?.replayAd !== null, channelId)
		} catch (error) {
			// handled in SourceAbstract
			throw error
		}
		return result 
	})

	sessionPauseAsync = flow(function* (sessionId, channelId, epgEventStart, playbackPosition, retryCount = NO_RETRIES) {
		let result = null
		try {
			const now = performance.now()
			result = yield CoCo.apiFetchPauseAd(sessionId, channelId, BigInt(epgEventStart), BigInt(Math.trunc(playbackPosition)))
			console.debug("sessionPauseAsyncCoCo %o", result?.sessionId, result)
			this._logDuration(now, "apiFetchPauseAd")
			this._logAdStats("Pause-Ad", result?.replayAd !== null, channelId)
		} catch (error) {
			// handled in SourceAbstract
			throw error
		}
		return result 
	})

	sessionAdZonesAsync = flow(function* (sessionId, epgEventStart, epgEventEnd, playbackPosition) {
		let result = null
		try {
			const now = performance.now()
			result = yield CoCo.apiFetchAdZones(sessionId, BigInt(epgEventStart), BigInt(epgEventEnd), BigInt(Math.trunc(playbackPosition)))
			console.debug("sessionAdZonesAsyncCoCo %o", result?.sessionId, result)
			this._logDuration(now, "apiFetchAdZones")
		} catch (error) {
			// handled in SourceAbstract
			throw error
		}
		return result 
	})

	sessionVerifyJumpAsync = flow(function* (sessionId, channelId, epgEventStart, epgEventEnd, from, to, inAdZone) {
		let result = null
		try {
			const now = performance.now()
			result = yield CoCo.apiVerifyJump(sessionId, BigInt(epgEventStart), BigInt(epgEventEnd), BigInt(Math.trunc(from)), BigInt(Math.trunc(to))	)
			console.debug("sessionVerifyJumpAsyncCoCo %o", result?.sessionId, result)
			this._logDuration(now, "apiVerifyJump")
			if (inAdZone) this._logAdStats("FFwd-Ad", result.fastForwardAd?.adUrl !== undefined, channelId)
		} catch (error) {
			// handled in SourceAbstract
			throw error
		}
		return result 
	})

	// MTVW-649: disabled / inactive
	sessionNotifyJumpAsync = flow(function* (sessionId, epgEventStart, epgEventEnd, from, to) {
		let result = null
		try {
			const now = performance.now()
			result = yield CoCo.apiNotifyJump(sessionId, BigInt(epgEventStart), BigInt(epgEventEnd), BigInt(Math.trunc(from)), BigInt(Math.trunc(to))	)
			console.debug("sessionNotifyJumpAsyncCoCo %o", result?.sessionId, result)
			this._logDuration(now, "apiNotifyJump")
		} catch (error) {
			// handled in SourceAbstract
			throw error
		}
		return //result 
	})

	sessionTrackingPauseAdAsync = flow(function* (sessionId, adsViewId, displayDuration, closeInteraction, err, retryCount) {
		let result = null
		try {
			result = yield CoCo.apiTrackPauseAd(sessionId, adsViewId, BigInt(displayDuration), closeInteraction, err)
			console.debug("sessionTrackingPauseAdAsyncCoCo %o", result?.sessionId, result)
		} catch (error) {
			// handled in SourceAbstract
			throw error
		}
	})

	sessionTrackingReplayAdAsync = flow(function* (sessionId, adsViewId, playbackTime, closeInteraction, err) {
		let result = null
		try {
			result = yield CoCo.apiTrackReplayAd(sessionId, adsViewId, BigInt(playbackTime), closeInteraction, err)
			console.debug("sessionTrackingReplayAdAsyncCoCo %o", result?.sessionId, result)
		} catch (error) {
			// handled in SourceAbstract
			throw error
		}
	})

	sessionTrackingFastForwardAdAsync = flow(function* (sessionId, adsViewId, playbackTime, closeInteraction, err) {
		let result = null
		try {
			result = yield CoCo.apiTrackFastForwardAd(sessionId, adsViewId, BigInt(playbackTime), closeInteraction, err)
			console.debug("sessionTrackingFastForwardAdAsyncCoCo %o", result?.sessionId, result)
		} catch (error) {
			// handled in SourceAbstract, SouceChannelReplay, SourceContentPlaylist
			throw error
		}
	})

	// MTVW-431: inactive, use derived information from channels MS
	adSkipInfoAsync = flow(function* (channelId, recordingId, epgEventStart, isSeriesRecording, bookingTime, replayWindow, isBookmark, retryCount = NO_RETRIES) {
	})

	// unused
	configurePauseMockAsync = flow(function* (enable = false) {
	})
}
